import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Home as HomeIcon, Compass } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Catch-all 404 route. Two jobs:
//  1. Emit `<meta name="robots" content="noindex, nofollow">` so Google DROPS
//     stale/zombie URLs (e.g. /uae_mortgage_2025, /off-plan-payment-...) that
//     used to exist under the removed /insights blog. Without this, the React
//     SPA fallback serves 200 + the homepage HTML for every URL and Google
//     indexes it as a soft-duplicate of "/".
//  2. Show a friendly bilingual page + auto-redirect home in 6s.
export default function NotFound() {
  const { lang, isRTL, lh } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const isAR = lang === "ar";

  useEffect(() => {
    const id = setTimeout(() => navigate(lh("/"), { replace: true }), 6000);
    return () => clearTimeout(id);
  }, [navigate, lh]);

  return (
    <>
      <Helmet>
        <title>{isAR ? "٤٠٤, الصفحة غير موجودة · بروبيرتك" : "404, Page Not Found · The Propertic"}</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="description" content={isAR ? "هذه الصفحة لم تعد موجودة على بروبيرتك." : "This page no longer exists on The Propertic."} />
      </Helmet>

      <section className="min-h-[80vh] grid place-items-center px-6" data-testid="not-found-page">
        <div className="max-w-lg text-center">
          <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-4">
            404 · {isAR ? "غير موجود" : "Not Found"}
          </p>
          <h1 className="font-editorial text-4xl sm:text-5xl text-propertic-navy leading-[1.1]">
            {isAR ? (
              <>هذا العنوان <span className="font-italic-serif text-propertic-gold">لا يوجد.</span></>
            ) : (
              <>This page <span className="font-italic-serif text-propertic-gold">isn't here.</span></>
            )}
          </h1>
          <p className="mt-5 text-propertic-ink-2/90 text-base leading-relaxed">
            {isAR
              ? `العنوان "${location.pathname}" لم يعد جزءاً من موقع بروبيرتك. سنُعيدكم إلى الصفحة الرئيسية خلال ٦ ثوانٍ.`
              : `The address "${location.pathname}" is no longer part of The Propertic. We'll take you home in 6 seconds.`}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to={lh("/")}
              data-testid="not-found-home-cta"
              className="btn-primary inline-flex items-center gap-2 h-11 px-6 rounded-full font-mono-brand text-[11px] tracking-[0.14em] uppercase"
            >
              <HomeIcon className="w-4 h-4" /> {isAR ? "الصفحة الرئيسية" : "Take me home"}
            </Link>
            <Link
              to={lh("/about")}
              data-testid="not-found-about-cta"
              className="btn-ghost inline-flex items-center gap-2 h-11 px-6 rounded-full font-mono-brand text-[11px] tracking-[0.14em] uppercase"
            >
              <Compass className={`w-4 h-4 ${isRTL ? "rtl-mirror" : ""}`} /> {isAR ? "من نحن" : "Who we are"}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
